import { useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar
} from '@carbon/react';
import cx from 'clsx';

import { routes } from 'core/routing';
import { useHasAccess } from 'core/user/role';
import SiteProjectsToolbarCell from 'features/all-projects/components/SiteProjectsToolbarCell';
import TableFiltersToolbar from 'features/all-projects/components/TableFiltersToolbar';
import { SORT } from 'features/all-projects/filters';
import useSiteProjectsController from 'features/all-projects/useSiteProjectsController';
import useProjectsQuery from 'shared/api/projects/useProjectsQuery';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import Link from 'shared/components/Link';
import OverlayLoader from 'shared/components/OverlayLoader';
import Pagination, { usePagination } from 'shared/components/Pagination';
import ProjectTier, { ProjectTierTooltip } from 'shared/components/ProjectTier';
import ProjectListTableToolbar from 'shared/project-list/ProjectListTableToolbar';
import { formatDate } from 'utils/dates';
import useDebouncedState from 'utils/hooks/useDebouncedState';
import styles from 'theme/features/all-projects/all-projects.module.scss';

const UnknownSitesTable = ({ isArchived, isConfidential, isFinal }) => {
  const { hasEditAccess } = useHasAccess();

  const pagination = usePagination();

  const [currentSort, setCurrentSort] = useState(SORT.startDate);
  const {
    value: searchValue,
    debouncedValue: currentSearchValue,
    setValue: setSearchValue,
    reset: resetSearchValue
  } = useDebouncedState({
    initialValue: '',
    onDebounce: pagination.resetPage
  });
  const {
    data: projects,
    isRefetching: isRefetchingProjects,
    refetch: refetchProjects
  } = useProjectsQuery({
    unknownSite: true,
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort: currentSort,
    archived: isArchived,
    confidented: isConfidential,
    finaled: isFinal,
    ...(currentSearchValue && { search: currentSearchValue })
  });

  const {
    duplicate,
    unarchive,
    archive,
    unconfidential,
    confidential,
    unfinal,
    final,
    isDuplicateLoading,
    isArchiveLoading,
    isConfidentialLoading,
    isFinalLoading
  } = useSiteProjectsController({
    projects,
    refetchProjects
  });

  const handleReset = () => {
    setCurrentSort(SORT.startDate);
    pagination.reset();
  };

  const tableData =
    projects?.map((x) => ({
      id: x.id.toString(),
      name: <Link href={routes.projectOutputs(x.id)}>{x.name}</Link>,
      tier: x.tier != null && <ProjectTier tier={x.tier} isValid={x.tierIsValid} />,
      label:
        x.isFinal && x.isConfidential ? (
          <span className={styles.label}>Final Version | Confidential</span>
        ) : x.isFinal ? (
          <span className={styles.label}>Final Version</span>
        ) : x.isConfidential ? (
          <span className={styles.label}>Confidential</span>
        ) : (
          ''
        ),
      location: `${x.city}, ${x.country}`,
      createdOn: formatDate(x.createdOn),
      createdBy: x.createdBy,
      updatedOn: x.updatedOn ? formatDate(x.updatedOn) : '-',
      updatedBy: x.updatedBy ?? '-'
    })) ?? [];

  const headers = [
    { key: 'name', header: 'Project Name' },
    { key: 'label', header: '' },
    {
      key: 'tier',
      header: (
        <>
          Tier
          <span style={{ top: 0, position: 'relative' }}>
            <ProjectTierTooltip />
          </span>
        </>
      )
    },
    { key: 'location', header: 'Location' },
    { key: 'createdOn', header: 'Created On' },
    { key: 'createdBy', header: 'Created By' },
    { key: 'updatedOn', header: 'Modified On' },
    { key: 'updatedBy', header: 'Modified By' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={tableData}
      size="sm"
      render={({
        rows,
        getTableContainerProps,
        getToolbarProps,
        selectAll,
        selectedRows,
        getTableProps,
        getSelectionProps,
        getHeaderProps,
        getRowProps
      }) => (
        <TableContainer
          className={cx(styles.table, styles['unknown-sites-table'])}
          {...getTableContainerProps()}
          title="Projects for Unknown Sites"
          description="All LCS projects and versions created without a known site"
        >
          <OverlayLoader
            isLoading={
              isRefetchingProjects || isDuplicateLoading || isArchiveLoading || isConfidentialLoading || isFinalLoading
            }
          />
          <TableToolbar {...getToolbarProps()} size="sm">
            <TableFiltersToolbar
              onReset={handleReset}
              onSortChange={setCurrentSort}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearchReset={() => {
                resetSearchValue();
                pagination.resetPage();
              }}
            />
            <ProjectListTableToolbar selectAll={selectAll} selectedRows={selectedRows} />
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableSelectAll {...getSelectionProps()} />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
                {hasEditAccess() && <TableToolbarCell.Header />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const project = projects?.[index];

                return (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    <TableSelectRow {...getSelectionProps({ row })} />
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                    {hasEditAccess() && (
                      <SiteProjectsToolbarCell
                        project={project}
                        onDuplicate={duplicate}
                        onArchive={archive}
                        onUnarchive={unarchive}
                        onConfidential={confidential}
                        onUnconfidential={unconfidential}
                        onFinal={final}
                        onUnfinal={unfinal}
                      />
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            isLastPage={!projects || projects.length < pagination.pageSize}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </TableContainer>
      )}
    />
  );
};

export default UnknownSitesTable;
